import { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { Accordion, Button, Dropdown, Icon, Label } from "semantic-ui-react";
import { styled } from "styled-components";

import { formatDate } from "simplydo/core/dates";
import { asMutation } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { RichTextViewer } from "simplydo/react/richtext";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { Checkbox } from "components/lib/UI";
import { DndContext } from "components/lib/dnd/DndContext";
import { Draggable } from "components/lib/dnd/Draggable";
import { Droppable } from "components/lib/dnd/Droppable";

type QuestionListProps = {
  questions: OpenAPI.Schemas["Question"][];
  challenge: OpenAPI.Schemas["Challenge"];
  selectedQuestions?: string[];
  setSelectedQuestions?: (any) => void;
  setQuestionToEdit?: (question: OpenAPI.Schemas["Question"]) => void;
  saveAndMakePublic?: (
    quesion: OpenAPI.Schemas["Question"],
    notifyUsers: boolean,
  ) => void;
  nonAdminView: boolean;
};

const StyledFieldIcon = styled(Icon)`
  margin-right: 0.78571429rem !important;
`;

const QuestionList = ({
  questions,
  challenge,
  setQuestionToEdit,
  selectedQuestions,
  setSelectedQuestions,
  saveAndMakePublic = () => {},
  nonAdminView,
}: QuestionListProps) => {
  const [activeQuestions, setActiveQuestions] = useState([]);
  const user = useAppSelector((state) => state.user);
  const isEditable = !!setQuestionToEdit;

  const canManageQuestions =
    util.hasPermission(user, "challenge.manageQuestions", challenge._id) ||
    util.hasPermission(user, "org.manageChallenges", challenge.organisation);

  const reorderQuestionsMutation = useMutation({
    mutationKey: ["challenges", challenge._id, "questions", "order"],
    mutationFn: asMutation(api.challenges.reorderQuestion)!,
    onSuccess: () => {
      api.invalidateQueries(api.challenges.getQuestions, [challenge._id]);
    },
  });

  const unpublishMutation = useMutation({
    mutationKey: ["challenges", challenge._id, "questions", "unpublish"],
    mutationFn: asMutation(api.challenges.unpublishQuestion)!,
    onSuccess: (result) => {
      api.invalidateQueries(api.challenges.get, [challenge._id]);
      api.setQueryData(
        api.challenges.getQuestions,
        [challenge._id],
        (prev) => ({
          ...prev,
          questions: prev.questions
            .filter((q) => q._id !== result.question._id)
            .concat([result.question]),
        }),
      );
      api.invalidateQueries(api.challenges.getQuestions, [challenge._id]);
    },
  });

  const deleteQuestionMutation = useMutation({
    mutationKey: ["challenges", challenge._id, "questions"],
    mutationFn: asMutation(api.challenges.deleteQuestion)!,
    onSuccess: (result) => {
      api.setQueryData(
        api.challenges.getQuestions,
        [challenge._id],
        (prev) => ({
          ...prev,
          questions: prev.questions.filter((q) => q._id !== result.deleted),
        }),
      );
      api.invalidateQueries(api.challenges.getQuestions, [challenge._id]);
    },
  });

  return (
    <Accordion fluid styled exclusive={false}>
      <DndContext
        onDragEnd={(event) => {
          const { active, over } = event;
          if (!active || !over) {
            return;
          }

          const sourceIndex = active.data.current.sortable.index;
          const destinationIndex = over.data.current.sortable.index;
          if (destinationIndex === undefined) return;
          if (sourceIndex === destinationIndex) return;

          const sourceQuestion = questions?.find(
            (question) => question._id === active.id,
          );
          if (sourceQuestion) {
            api.setQueryData(
              api.challenges.getQuestions,
              [challenge._id],
              (prev) => ({
                ...prev,
                questions: prev.questions
                  .filter((q) => q._id !== active.id)
                  .flatMap((q) => {
                    // Respect move within same list
                    if (sourceQuestion.published && !q.published) {
                      return [q];
                    }
                    if (!sourceQuestion.published && q.published) {
                      return [q];
                    }
                    if (q.order === destinationIndex) {
                      if (sourceIndex < destinationIndex) {
                        return [q, sourceQuestion];
                      }
                      return [sourceQuestion, q];
                    }
                    return [q];
                  }),
              }),
            );

            reorderQuestionsMutation.mutate([
              challenge._id,
              active.id as string,
              destinationIndex,
            ]);
          }
        }}
      >
        <Droppable id="questions" itemIds={questions?.map((q) => q._id)}>
          {questions?.map((question) => (
            <Draggable
              key={question._id}
              id={question._id}
              disabled={!isEditable || !canManageQuestions}
            >
              <Accordion.Title
                active={activeQuestions.includes(question._id)}
                index={question.order}
                style={{ color: "rgba(0, 0, 0, .87)" }}
                onClick={() => {
                  setActiveQuestions((prev) => {
                    if (prev.includes(question._id)) {
                      return prev.filter((i) => i !== question._id);
                    }
                    return [...prev, question._id];
                  });
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ maxWidth: "85%", alignSelf: "center" }}>
                    {isEditable && canManageQuestions ?
                      <div
                        style={{
                          marginBottom: 10,
                          fontWeight: "normal",
                          color: "grey",
                        }}
                      >
                        <UserChip
                          user={question.ownerCreatedBy}
                          compact="very"
                        />{" "}
                        asked on {formatDate(question.createdAt, "MMM d, yyyy")}
                        :
                      </div>
                    : null}
                    <div>
                      <Icon name="dropdown" />
                      <span style={{ fontSize: "1em" }}>
                        {question.question}
                      </span>
                      <span
                        style={{
                          marginLeft: 12,
                          position: "relative",
                          top: -2,
                        }}
                      >
                        {question.announcedAt && canManageQuestions && (
                          <Label size="mini" color="blue">
                            Last announced{" "}
                            {formatDate(question.announcedAt, "MMM d, yyyy")}
                          </Label>
                        )}
                      </span>
                      <span style={{ marginLeft: 12 }} />
                      <span style={{ position: "relative", top: -2 }}>
                        {!question.answer ?
                          <Label content="Awaiting answer" size="mini" />
                        : null}
                        {canManageQuestions ?
                          !question.published ?
                            <Label
                              content="Not public"
                              size="mini"
                              color="yellow"
                            />
                          : null
                        : null}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: isEditable ? 5 : 0,
                      gap: 3,
                    }}
                  >
                    {isEditable && canManageQuestions ?
                      <Dropdown
                        direction="left"
                        icon={null}
                        trigger={
                          <Button basic icon>
                            <Icon name="chevron down" />
                          </Button>
                        }
                      >
                        <Dropdown.Menu>
                          {question.published ?
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                util
                                  .confirm(
                                    "Make question non-public",
                                    "Are you sure you want to unpublish this question? Users will no longer be able to see the question or its response.",
                                  )
                                  .then(() => {
                                    unpublishMutation.mutate([
                                      challenge._id,
                                      question._id,
                                    ]);
                                  })
                                  .catch(() => {});
                              }}
                            >
                              <Icon name="undo" />
                              Make non-public
                            </Dropdown.Item>
                          : question.answer ?
                            <Dropdown.Item>
                              <Dropdown
                                icon={null}
                                // @ts-ignore
                                text={
                                  <>
                                    <StyledFieldIcon name="paper plane" />
                                    Make public
                                  </>
                                }
                              >
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() =>
                                      saveAndMakePublic(question, true)
                                    }
                                  >
                                    <Icon name="bell" />
                                    Make public and notify users
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() =>
                                      saveAndMakePublic(question, false)
                                    }
                                  >
                                    <Icon name="bell slash" />
                                    Make public without notifying users
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </Dropdown.Item>
                          : <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                setQuestionToEdit(question);
                              }}
                            >
                              <Icon name="reply all" />
                              Answer now
                            </Dropdown.Item>
                          }
                          <Dropdown.Item
                            onClick={(e) => {
                              e.stopPropagation();
                              setQuestionToEdit(question);
                            }}
                          >
                            <Icon name="edit" />
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={(e) => {
                              e.stopPropagation();
                              util
                                .confirm(
                                  "Delete question",
                                  "Are you sure you want to delete this question?",
                                )
                                .then(() => {
                                  deleteQuestionMutation.mutate([
                                    challenge._id,
                                    question._id,
                                  ]);
                                })
                                .catch(() => {});
                            }}
                          >
                            <Icon name="trash" />
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    : null}
                    {!nonAdminView &&
                      canManageQuestions &&
                      setSelectedQuestions &&
                      question.answer && (
                        <Checkbox
                          isSelected={selectedQuestions.includes(question._id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            !selectedQuestions.includes(question._id) ?
                              setSelectedQuestions((prev) => [
                                ...prev,
                                question._id,
                              ])
                            : setSelectedQuestions((prev) =>
                                prev.filter((q) => q !== question._id),
                              );
                          }}
                        />
                      )}
                  </div>
                </div>
              </Accordion.Title>
              <Accordion.Content
                active={activeQuestions.includes(question._id)}
                style={{ paddingTop: 0, marginLeft: 20, paddingBottom: 5 }}
              >
                {question.answer ?
                  <RichTextViewer value={question.answer || ""} />
                : <>
                    <p style={{ color: "#ababab" }}>
                      Question has not been answered yet.
                    </p>
                    {isEditable && canManageQuestions ?
                      <Button
                        size="tiny"
                        primary
                        onClick={() => {
                          setQuestionToEdit(question);
                        }}
                      >
                        Answer now
                      </Button>
                    : null}
                  </>
                }
              </Accordion.Content>
            </Draggable>
          ))}
        </Droppable>
      </DndContext>
    </Accordion>
  );
};

export default QuestionList;
